"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import type { Addon } from "@/lib/types";

const ICONS: Record<string, ReactNode> = {
  clm: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
    </svg>
  ),
  utils: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
    </svg>
  ),
};
const FALLBACK_ICON = ICONS.utils;

function launchIcon(icon: string): ReactNode {
  return ICONS[icon] ?? FALLBACK_ICON;
}

export default function AddonLauncher({ collapsed }: { collapsed?: boolean }) {
  const { getAccessToken, user } = useAuth();
  const pathname = usePathname();
  const [addons, setAddons] = useState<Addon[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const mountedRef = useRef(true);

  // Clear mountedRef on unmount so the launch catch doesn't set state on a dead
  // component — and SET it on mount: React dev StrictMode mounts, unmounts and
  // re-mounts, and a ref only cleared left the spinner running forever after a
  // successful launch (staging, 2026-09-29).
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // The list is per (identity, workspace), so re-ask whenever either changes.
  // It used to be fetched ONCE when the dashboard layout mounted, which survives
  // client-side navigation: a superadmin who started impersonating from
  // /admin/users kept their OWN (empty) list, and a member who landed on a
  // personal workspace and switched to one with Utils never saw the launcher
  // (2026-09-28, Nichiha).
  const userId = user?.id ?? null;
  const orgSlug = pathname?.match(/^\/o\/([^/]+)/)?.[1] ?? null;
  useEffect(() => {
    let cancelled = false;
    // Never show the previous workspace's launchers while the new list loads.
    setAddons([]);
    if (!userId) return;
    (async () => {
      try {
        const list = await apiFetch<Addon[]>("/addons", { token: getAccessToken() ?? undefined });
        if (!cancelled) setAddons(list);
      } catch {
        /* no launchers on error */
      }
    })();
    return () => { cancelled = true; };
  }, [getAccessToken, userId, orgSlug]);

  const launch = async (audience: string) => {
    setBusy(audience);
    // Open the tab synchronously (inside the click gesture) so the browser
    // doesn't block it as a popup, then point it at the SSO redirect once the
    // ticket is minted. Keeps SignForge open in the original tab.
    const tab = window.open("", "_blank");
    if (tab) {
      try {
        tab.document.write(
          '<!doctype html><meta charset="utf-8"><title>Signing you in…</title>' +
          '<body style="margin:0;font:15px system-ui,sans-serif;color:#475569;' +
          'display:flex;align-items:center;justify-content:center;height:100vh">Signing you in…</body>',
        );
      } catch {
        /* about:blank write may be restricted — harmless */
      }
    }
    try {
      const res = await apiFetch<{ redirect_url: string }>(`/sso/${audience}/ticket`, {
        method: "POST",
        token: getAccessToken() ?? undefined,
      });
      if (tab) tab.location.href = res.redirect_url;
      else window.location.href = res.redirect_url; // popup blocked → same-tab fallback
    } catch {
      if (tab) {
        try { tab.close(); } catch { /* ignore */ }
      }
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };

  if (addons.length === 0) return null;

  return (
    <>
      {addons.map((a) => (
        <button
          key={a.audience}
          type="button"
          onClick={() => launch(a.audience)}
          disabled={busy === a.audience}
          title={collapsed ? a.name : undefined}
          className={`flex w-full items-center rounded-lg text-[13px] font-medium transition-all duration-150 text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-60 ${
            collapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2"
          }`}
        >
          <span className="shrink-0 text-slate-400">
            {busy === a.audience ? (
              <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
              </svg>
            ) : launchIcon(a.icon)}
          </span>
          {!collapsed && a.name}
          {!collapsed && (
            <svg className="h-3 w-3 text-slate-400 ml-auto" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          )}
        </button>
      ))}
    </>
  );
}
